import React, { useMemo, useState } from "react";
import { Button, Col, Collapse, DatePicker, Form, Input, Row, Select } from "antd";
import type { FormInstance } from "antd";
import { useAppSelector } from "../../..";
import type { AnyObject, DictionaryDto } from "../../../lib/interfaces";
import { ActiveSearchFormItem, AgreementsSearchFormItem } from "../../BulkSearchItems";
import { checkIsLoaded, checkIsLoading, displayFormat } from "../../../lib/utils";
import InputNumberFormat from "../../common/InputNumberFormat";
import { IClientType, ISearchFilterField } from "../../../lib/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import "./BulkBillsSearchFilters.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

interface ComponentProps {
  form: FormInstance;
  billsSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  filtersCollapseState: string;
  setFiltersCollapsState: React.Dispatch<React.SetStateAction<string>>;
  resetSearchPaymentsAndResetSetSelectedRowKeys: () => void;
}

export const BulkBillsSearchFilters = (props: ComponentProps) => {
  const {
    form,
    billsSearchParams,
    onSearch,
    filtersCollapseState,
    setFiltersCollapsState,
    resetSearchPaymentsAndResetSetSelectedRowKeys,
  } = props;

  const { rateModesEventsLoading } = useAppSelector((state) => state.rtkDictionaries);

  const { tenant } = useAppSelector((state) => state.tenants);

  const serviceTypes = (useAppSelector(
    (state) => state.service.serviceTypes
  ) as unknown) as DictionaryDto[];

  const billTypes = (useAppSelector(
    (state) => state.rtkDictionaries.billType
  ) as unknown) as DictionaryDto[];

  const payStatuses = (useAppSelector(
    (state) => state.rtkDictionaries.payStatus
  ) as unknown) as DictionaryDto[];

  const billStatuses = (useAppSelector(
    (state) => state.rtkDictionaries.billStatus
  ) as unknown) as DictionaryDto[];

  const rateModesEvents = (useAppSelector(
    (state) => state.rtkDictionaries.rateModesEvents
  ) as unknown) as DictionaryDto[];

  const { billsLoading } = useAppSelector((state) => state.bills);

  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapsState(newState);
  };

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Клиент",
        fieldElement: (
          <AgreementsSearchFormItem
            name="accountIds"
            multiselect
            combineAgreements
            clientType={clientType}
            setClientType={setClientType}
            onChange={() => {
              form.setFieldsValue({
                "organization.property.stringValue": undefined,
                "subscriber.externalId": undefined,
              });
            }}
          />
        ),
        visible: true,
      },
      {
        label: "Номер счета",
        fieldElement: (
          <Form.Item label="Номер счета" name="billNumber">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Период счета",
        fieldElement: (
          <Form.Item label="Период счета" name="periodDate">
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={displayFormat}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: tenant?.id === 1 ? "БИН" : "ИНН",
        fieldElement: (
          <ActiveSearchFormItem
            name="organization.property.stringValue"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "subscriber.externalId": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: false,
      },
      {
        label: "Тип счёта",
        fieldElement: (
          <Form.Item label="Тип счёта" name="billTypeId">
            <Select allowClear>
              {billTypes &&
                billTypes.length !== 0 &&
                billTypes.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата счёта",
        fieldElement: (
          <Form.Item label="Дата счёта" name="fromToDate">
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={displayFormat}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тех. ID",
        labelTitle: "Технический ID",
        fieldElement: (
          <ActiveSearchFormItem
            name="subscriber.externalId"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "organization.property.stringValue": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: true,
      },
      {
        label: "Сумма счета",
        fieldElement: (
          <Form.Item label="Сумма счета">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="amountFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
              <Col span={3} offset={1}>
                <label>по:</label>
              </Col>
              <Col span={10}>
                <Form.Item name="amountTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Статус оплаты",
        fieldElement: (
          <Form.Item label="Статус оплаты" name="payStatusIds">
            <Select placeholder="Выбрать" allowClear showArrow mode="multiple">
              {payStatuses &&
                payStatuses.length !== 0 &&
                payStatuses.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Вид цены",
        fieldElement: (
          <Form.Item label="Вид цены" name="service.rateModeId">
            <Select showSearch placeholder="Выбрать" allowClear>
              {checkIsLoaded(rateModesEventsLoading) &&
                rateModesEvents.map((item) => (
                  <Select.Option key={item.id} value={item.id} id={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Статус счёта",
        fieldElement: (
          <Form.Item label="Статус счёта" name="billStatusIds">
            <Select placeholder="Выбрать" allowClear showArrow mode="multiple">
              {billStatuses &&
                billStatuses.length !== 0 &&
                billStatuses.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата оплаты",
        fieldElement: (
          <Form.Item label="Дата оплаты" name="paidDate">
            <DatePicker.RangePicker style={{ width: "100%" }} format={displayFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип продукта",
        fieldElement: (
          <Form.Item label="Тип продукта" name="service.serviceType">
            <Select
              placeholder="Выбрать"
              allowClear
              showArrow
              mode="multiple"
              showSearch
              filterOption={(input, option) =>
                // @ts-ignore
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {serviceTypes &&
                serviceTypes.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID счетов",
        fieldElement: (
          <Form.Item
            label="ID счетов"
            name="id"
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "В качестве разделителя может использоваться только запятая",
              },
            ]}
          >
            <Input placeholder='Введите ID счетов через ","' allowClear />
          </Form.Item>
        ),
        visible: true,
      },
    ],
    [tenant?.id, billTypes, payStatuses, rateModesEvents, billStatuses, serviceTypes, clientType]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>([]);

  return (
    <div className="bulkBills-searchFilters">
      <Form
        form={form}
        labelAlign="left"
        initialValues={billsSearchParams}
        {...layout}
        onFinish={onSearch}
      >
        <div className="inner-row">
          <Collapse
            defaultActiveKey="1"
            onChange={toggleFiltersCollapseState}
            expandIconPosition="right"
            className="filter-collapse"
          >
            <Collapse.Panel header={getPanelHeader()} key="1">
              <Row justify="end">
                <BulkFiltersToggler
                  allFields={allFields}
                  setFields={setFields}
                  filterName="bulk-bills"
                />
              </Row>
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={8} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSetSelectedRowKeys}>
                  Сброс параметров
                </Button>
                <Button loading={checkIsLoading(billsLoading)} type="primary" htmlType="submit">
                  Применить
                </Button>
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </div>
      </Form>
    </div>
  );
};
